/* ==========================================================================
   Nubirox — sections.js
   Motion and interaction for the editorial sections: line reveals, parallax,
   counters, the sticky scroll story, hotspots, the sticky CTA rail,
   accessory multi-add and the market selector.
   Depends on theme.js (loaded first) for Cart, Drawer and formatMoney.
   ========================================================================== */
(function () {
  'use strict';

  var theme = window.theme || {};

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function on(el, type, handler, options) {
    if (el) el.addEventListener(type, handler, options);
  }

  function motionOff() {
    return (
      theme.motion === 'off' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function money(cents) {
    return theme.formatMoney ? theme.formatMoney(cents) : (cents / 100).toFixed(2);
  }

  /* ---------------------------------------------------------- line reveal */

  function initLineReveal(scope) {
    var items = $$('.line-reveal', scope || document);
    if (!items.length) return;

    if (motionOff() || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
    );

    items.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------ parallax */

  var parallaxItems = [];
  var parallaxTicking = false;

  function updateParallax() {
    var viewportHeight = window.innerHeight;

    parallaxItems.forEach(function (item) {
      var rect = item.el.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > viewportHeight + 200) return;
      var centre = rect.top + rect.height / 2 - viewportHeight / 2;
      item.el.style.transform = 'translate3d(0, ' + (-centre * item.speed).toFixed(2) + 'px, 0)';
    });

    parallaxTicking = false;
  }

  function initParallax(scope) {
    if (motionOff() || theme.parallax === false) return;

    $$('[data-parallax]', scope || document).forEach(function (el) {
      if (el.dataset.parallaxReady === 'true') return;
      el.dataset.parallaxReady = 'true';
      el.classList.add('parallax');
      parallaxItems.push({ el: el, speed: parseFloat(el.getAttribute('data-parallax')) || 0.12 });
    });

    if (!parallaxItems.length) return;
    updateParallax();
  }

  on(
    window,
    'scroll',
    function () {
      if (!parallaxItems.length || parallaxTicking) return;
      parallaxTicking = true;
      window.requestAnimationFrame(updateParallax);
    },
    { passive: true }
  );

  on(window, 'resize', function () {
    if (parallaxItems.length) window.requestAnimationFrame(updateParallax);
  });

  /* ------------------------------------------------------------ counters */

  function initCounters(scope) {
    var counters = $$('[data-count-to]', scope || document).filter(function (el) {
      return el.dataset.countReady !== 'true';
    });
    if (!counters.length) return;

    function paint(el, value) {
      var decimals = parseInt(el.getAttribute('data-count-decimals'), 10) || 0;
      el.textContent =
        (el.getAttribute('data-count-prefix') || '') +
        value.toFixed(decimals) +
        (el.getAttribute('data-count-suffix') || '');
    }

    function run(el) {
      el.dataset.countReady = 'true';
      var target = parseFloat(el.getAttribute('data-count-to')) || 0;

      if (motionOff()) {
        paint(el, target);
        return;
      }

      var duration = 1300;
      var start = null;

      function frame(now) {
        if (start === null) start = now;
        var progress = Math.min((now - start) / duration, 1);
        // easeOutExpo — fast start, long settle.
        var eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
        paint(el, target * eased);
        if (progress < 1) window.requestAnimationFrame(frame);
      }

      window.requestAnimationFrame(frame);
    }

    if (!('IntersectionObserver' in window)) {
      counters.forEach(run);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          run(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.4 }
    );

    counters.forEach(function (el) {
      paint(el, 0);
      observer.observe(el);
    });
  }

  /* -------------------------------------------------------- scroll story */

  function initStory(scope) {
    $$('[data-story]', scope || document).forEach(function (root) {
      if (root.dataset.storyReady === 'true') return;
      root.dataset.storyReady = 'true';

      var steps = $$('[data-story-step]', root);
      var frames = $$('[data-story-frame]', root);
      var indexLabel = $('[data-story-index]', root);
      if (!steps.length) return;

      function activate(index) {
        steps.forEach(function (step, i) {
          step.classList.toggle('is-active', i === index);
        });
        frames.forEach(function (frame, i) {
          frame.classList.toggle('is-active', i === index);
        });
        if (indexLabel) {
          indexLabel.textContent = String(index + 1).padStart(2, '0') + ' / ' + String(steps.length).padStart(2, '0');
        }
      }

      activate(0);

      if (!('IntersectionObserver' in window)) {
        steps.forEach(function (step) {
          step.classList.add('is-active');
        });
        return;
      }

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            activate(steps.indexOf(entry.target));
          });
        },
        { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
      );

      steps.forEach(function (step) {
        observer.observe(step);
      });
    });
  }

  /* ------------------------------------------------------------ hotspots */

  function initHotspots(scope) {
    $$('[data-hotspots]', scope || document).forEach(function (root) {
      if (root.dataset.hotspotsReady === 'true') return;
      root.dataset.hotspotsReady = 'true';

      var dots = $$('[data-hotspot]', root);
      var panels = $$('[data-hotspot-panel]', root);
      if (!dots.length) return;

      function activate(index) {
        dots.forEach(function (dot, i) {
          dot.setAttribute('aria-pressed', String(i === index));
        });
        panels.forEach(function (panel, i) {
          panel.classList.toggle('is-active', i === index);
        });
      }

      dots.forEach(function (dot, i) {
        on(dot, 'click', function () {
          activate(i);
        });
        on(dot, 'mouseenter', function () {
          if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) activate(i);
        });
      });

      panels.forEach(function (panel, i) {
        on(panel, 'mouseenter', function () {
          if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) activate(i);
        });
      });

      activate(0);
    });
  }

  /* ----------------------------------------------------- sticky CTA rail */

  function initRail(scope) {
    $$('[data-rail]', scope || document).forEach(function (rail) {
      if (rail.dataset.railReady === 'true') return;
      rail.dataset.railReady = 'true';

      var afterSelector = rail.getAttribute('data-rail-after');
      var sentinel = afterSelector ? $(afterSelector) : null;

      if (sentinel && 'IntersectionObserver' in window) {
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              rail.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
            });
          },
          { threshold: 0 }
        );
        observer.observe(sentinel);
        return;
      }

      var ticking = false;
      function update() {
        rail.classList.toggle('is-visible', window.scrollY > window.innerHeight * 0.7);
        ticking = false;
      }
      on(
        window,
        'scroll',
        function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(update);
        },
        { passive: true }
      );
      update();
    });
  }

  /* ------------------------------------------------------ accessory add */

  function initUpsell(scope) {
    $$('[data-upsell]', scope || document).forEach(function (root) {
      if (root.dataset.upsellReady === 'true') return;
      root.dataset.upsellReady = 'true';

      var inputs = $$('input[type="checkbox"]', root);
      var totalEl = $('[data-upsell-total]', root);
      var button = $('[data-upsell-add]', root);
      var countEl = $('[data-upsell-count]', root);
      if (!inputs.length || !button) return;

      function selected() {
        return inputs.filter(function (input) {
          return input.checked;
        });
      }

      function refresh() {
        var chosen = selected();
        var total = chosen.reduce(function (sum, input) {
          return sum + (parseInt(input.getAttribute('data-price'), 10) || 0);
        }, 0);
        if (totalEl) totalEl.textContent = money(total);
        if (countEl) countEl.textContent = chosen.length;
        button.disabled = chosen.length === 0;
      }

      inputs.forEach(function (input) {
        on(input, 'change', refresh);
      });

      on(button, 'click', function () {
        var chosen = selected();
        if (!chosen.length || !theme.Cart) return;

        theme.setButtonState(button, 'is-loading');

        theme.Cart.add(
          chosen.map(function (input) {
            return { id: parseInt(input.value, 10), quantity: 1 };
          })
        )
          .then(function () {
            theme.setButtonState(button, 'is-done');
            inputs.forEach(function (input) {
              input.checked = false;
            });
            setTimeout(function () {
              theme.setButtonState(button, null);
              refresh();
            }, 1500);
            if (theme.cartDrawer && theme.Drawer) theme.Drawer.open('CartDrawer');
          })
          .catch(function (error) {
            theme.setButtonState(button, null);
            if (theme.toast) theme.toast(error.message || 'Something went wrong.', 'alert');
          });
      });

      refresh();
    });
  }

  /* --------------------------------------------------- market selector -- */

  function initLocale(scope) {
    $$('[data-locale]', scope || document).forEach(function (root) {
      if (root.dataset.localeReady === 'true') return;
      root.dataset.localeReady = 'true';

      var button = $('[data-locale-button]', root);
      var form = root.closest('form') || $('form', root);

      on(button, 'click', function (event) {
        event.stopPropagation();
        root.classList.toggle('is-open');
        button.setAttribute('aria-expanded', String(root.classList.contains('is-open')));
      });

      $$('[data-locale-value]', root).forEach(function (option) {
        on(option, 'click', function () {
          var input = $('[data-locale-input]', root);
          if (input && form) {
            input.value = option.getAttribute('data-locale-value');
            form.submit();
          }
        });
      });
    });

    if (!document.localeCloseBound) {
      document.localeCloseBound = true;
      document.addEventListener('click', function (event) {
        $$('.locale.is-open').forEach(function (root) {
          if (!root.contains(event.target)) {
            root.classList.remove('is-open');
            var button = $('[data-locale-button]', root);
            if (button) button.setAttribute('aria-expanded', 'false');
          }
        });
      });
    }
  }

  /* ------------------------------------------------------ review bars --- */

  function initReviewBars(scope) {
    $$('[data-review-bars]', scope || document).forEach(function (root) {
      if (root.dataset.barsReady === 'true') return;
      root.dataset.barsReady = 'true';

      var fills = $$('[data-bar-fill]', root);

      function paint() {
        fills.forEach(function (fill) {
          fill.style.width = (fill.getAttribute('data-value') || 0) + '%';
        });
      }

      if (!('IntersectionObserver' in window)) {
        paint();
        return;
      }

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            paint();
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.3 }
      );

      observer.observe(root);
    });
  }

  /* --------------------------------------------------------------- init  */

  function init(scope) {
    initLineReveal(scope);
    initParallax(scope);
    initCounters(scope);
    initStory(scope);
    initHotspots(scope);
    initRail(scope);
    initUpsell(scope);
    initLocale(scope);
    initReviewBars(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  theme.initSections = init;
  window.theme = theme;
})();
